import API, { Types } from '@/modules/API';
import {
  REPORTING_QUERY_VERSION,
  setDeviceCommonFields,
  setCabinetFields
} from '@/store/modules/Reports';
import Utils from '@/modules/Utils';

const STREAM_ITEMS_LIMIT = 999999999;

/**
 * Header columns — eager fetch on every Cabinet Details mount.
 * Mix of cross-page subset (mirrored to store) + component-only modified
 * timestamps (sys___*__modified) used by the page header.
 */
export const HEADER_COLUMNS = [
  // Cross-page (mirrored to store)
  'id',
  'class_name',
  'meta.category',
  'meta.commission',
  'meta.device',
  'sys___active',
  'sys___connected',
  'services.config',
  'meta.devices_schedule',
  // Component-state-only display fields
  'sys___active__modified',
  'sys___connected__modified',
  'name',
  'meta.location',
  // Needed for the always-visible operation-state header (Retrofit /
  // Partial Retrofit / Pending Verification) — fetching it lazily via the
  // Advanced tab left the header blank until that tab was opened.
  'cabinet.operation_state'
];

/** Header columns when TTL hits — only the component-only display fields. */
export const HEADER_COLUMNS_FRESH = [
  'id',
  'sys___active__modified',
  'sys___connected__modified',
  'cabinet.operation_state'
];

/**
 * Columns that don't apply to virtual cabinets — virtual cabinets have no
 * sys___active/sys___connected stream, so requesting them is pure waste.
 */
const VIRTUAL_CABINET_SKIP_COLUMNS = new Set([
  'sys___active',
  'sys___connected',
  'sys___active__modified',
  'sys___connected__modified'
]);

/** Fixture columns for the per-cabinet fixtures query. */
export const FIXTURE_COLUMNS = [
  'id',
  'class_name',
  'sys___active',
  'sys___connected',
  'meta.category',
  'meta.commission',
  'meta.device',
  'meta.location',
  'name',
  'power',
  // Icon-determining fields. getMapIcon reads these for disconnected fixtures
  // (deviceHasSchedule / schedule-on checks); without them it throws and the
  // marker falls back to a gray default icon on the FullView map.
  'device.schedule',
  'meta.schedule_curve',
  'disconnect_reason',
  // Retrofit / Pending Verification override the connectivity-derived icon
  // (getMapIcon -> getRetrofitIcon). Without it the FullView map paints the
  // real connectivity icon first and only swaps to the retrofit one once the
  // global fixtures stream (which does request this column) merges in.
  'pole.operation_state'
];

/**
 * Per-tab lazy column sets. Most are component-state-only (consumed only by
 * Cabinet Details). A few overlap with the cross-page subset (none currently
 * — cross-page subset is fully fetched in HEADER_COLUMNS).
 */
export const TAB_COLUMNS: Record<string, string[]> = {
  'energy': ['live_data', 'live_data__modified', 'total_energy', 'total_energy__modified'],
  'cabinet-details': ['cabinet.operation_state'],
  'connections': [
    'device.connections',
    'device.schedule',
    'device.satec.dio',
    'device.version.os_release',
    'digital_input',
    'digital_input__modified',
    'control',
    'control__modified'
  ],
  'control': [
    'device.connections',
    'device.schedule',
    'device.satec.dio',
    'device.version.os_release',
    'digital_input',
    'digital_input__modified',
    'control',
    'control__modified'
  ],
  'advanced': [
    'device.version',
    'device.version.os_release',
    'electricity.price',
    'cabinet.operation_state',
    'live_data',
    'live_data__modified',
    'digital_input',
    'digital_input__modified',
    'control',
    'control__modified'
  ]
};

const reportingPath = (companyId: string, projectId: string) =>
  `/${companyId}/projects/${projectId}/streaming_reporting_query/`;

interface FetchParams {
  companyId: string;
  projectId: string;
  defaultLocation?: any;
}

/**
 * Generic single-row reporting query for a cabinet by id with caller-provided
 * columns. Returns the row (with cabinet field defaults applied) or null.
 */
export async function fetchCabinetColumns(
  params: FetchParams & { id: string; columns: string[] }
): Promise<any | null> {
  const { companyId, projectId, id, columns, defaultLocation } = params;
  const body: any = {
    columns,
    where: [`id='${id}'`],
    items: STREAM_ITEMS_LIMIT,
    version: REPORTING_QUERY_VERSION
  };
  const result = await API.streamingPost(
    Types.COMPANIES,
    reportingPath(companyId, projectId),
    body,
    {}
  );
  const list = Array.isArray(result) ? result : [];
  if (!list.length) return null;
  const row = list[0];
  // Apply the same field-defaulting the global stream applies, so consumers
  // don't have to defend against missing optional fields.
  setDeviceCommonFields(row, defaultLocation);
  if (row['meta.category'] && Utils.hasCabinetClass(row['meta.category'].category)) {
    setCabinetFields(row);
  }
  return row;
}

/** Convenience for the eager mount fetch. */
export async function fetchCabinetHeader(
  params: FetchParams & { id: string; fresh?: boolean; isVirtual?: boolean }
): Promise<any | null> {
  let columns = params.fresh ? HEADER_COLUMNS_FRESH : HEADER_COLUMNS;
  if (params.isVirtual) {
    columns = columns.filter((c) => !VIRTUAL_CABINET_SKIP_COLUMNS.has(c));
  }
  return fetchCabinetColumns({ ...params, columns });
}

/** Strip sys___active/sys___connected columns from an arbitrary column set. */
export function filterColumnsForVirtual(columns: string[]): string[] {
  return columns.filter((c) => !VIRTUAL_CABINET_SKIP_COLUMNS.has(c));
}

/* -------------------------------------------------------------------------- */
/* Per-tab in-memory cache                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Mirrors the 20-min header TTL. Tab data is mostly cabinet-only fields
 * (operation_state, tariff, services config snapshots, …) that don't stream
 * via MQTT, so caching across navigations avoids redundant reporting queries
 * on quick back-and-forth flows. Live streams (live_data, control, …) keep
 * being updated by MQTT after the cached seed.
 */
export const TAB_CACHE_TTL_MS = 10 * 60 * 1000;

interface TabCacheEntry {
  row: any;
  fetchedAt: number;
}

const tabCache: Map<string, TabCacheEntry> = new Map();

const tabKey = (deviceId: string, tabName: string) => `${deviceId}::${tabName}`;

/** True if cached row for (deviceId, tabName) exists and is within TTL. */
export function isTabSnapshotFresh(deviceId: string, tabName: string): boolean {
  const entry = tabCache.get(tabKey(deviceId, tabName));
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < TAB_CACHE_TTL_MS;
}

/** Returns the cached row for (deviceId, tabName), or null. */
export function getTabSnapshot(deviceId: string, tabName: string): any | null {
  const entry = tabCache.get(tabKey(deviceId, tabName));
  return entry ? entry.row : null;
}

/** Cache the row for (deviceId, tabName) with a fresh timestamp. */
export function primeTabSnapshot(deviceId: string, tabName: string, row: any): void {
  if (!deviceId || !tabName || !row) return;
  tabCache.set(tabKey(deviceId, tabName), { row, fetchedAt: Date.now() });
}

/** Drop all cached tab rows for a device (e.g. after a user-initiated save). */
export function invalidateDeviceTabCache(deviceId: string): void {
  if (!deviceId) return;
  const prefix = `${deviceId}::`;
  Array.from(tabCache.keys()).forEach((k) => {
    if (k.startsWith(prefix)) tabCache.delete(k);
  });
  // A save mutates cabinet/fixture data, so the standalone fetch snapshot is
  // stale too — force the next page entry to re-fetch.
  cabinetDetailFetchedCache.delete(deviceId);
}

/* -------------------------------------------------------------------------- */
/* Cabinet-detail on-demand fetch cache                                       */
/* -------------------------------------------------------------------------- */

/**
 * Records when Cabinet Details last ran its OWN on-demand fetch (header +
 * fixtures) for a device. The `cabinetCrossPageFresh` store getter is anchored
 * to the project-wide stream's completion time, which says nothing about our
 * standalone fetch — so without this cache every re-entry to the page refetches
 * regardless of TTL. Mirrors the 15-min header/tab TTL.
 */
const cabinetDetailFetchedCache: Map<string, number> = new Map();

/** True if Cabinet Details fetched this cabinet within the TTL window. */
export function isCabinetDetailFresh(deviceId: string): boolean {
  const fetchedAt = cabinetDetailFetchedCache.get(deviceId);
  if (!fetchedAt) return false;
  return Date.now() - fetchedAt < TAB_CACHE_TTL_MS;
}

/** Stamp the cabinet-detail fetch time (call after a successful full fetch). */
export function primeCabinetDetailFetched(deviceId: string): void {
  if (!deviceId) return;
  cabinetDetailFetchedCache.set(deviceId, Date.now());
}

/* -------------------------------------------------------------------------- */
/* Cabinet header (sys___*__modified) cache                                   */
/* -------------------------------------------------------------------------- */

/**
 * sys___active__modified / sys___connected__modified are component-only
 * display fields, NOT part of the cross-page subset stored in reportsList.
 * Without a dedicated cache, every quick re-entry to the page would refetch
 * them via HEADER_COLUMNS_FRESH. They change rarely (only when the cabinet
 * connects/disconnects), so caching with the same 20-min TTL is safe.
 */
interface HeaderModifiedCacheEntry {
  sys___active__modified: any;
  sys___connected__modified: any;
  'cabinet.operation_state': any;
  fetchedAt: number;
}

const headerModifiedCache: Map<string, HeaderModifiedCacheEntry> = new Map();

export function isHeaderModifiedFresh(deviceId: string): boolean {
  const entry = headerModifiedCache.get(deviceId);
  if (!entry) return false;
  return Date.now() - entry.fetchedAt < TAB_CACHE_TTL_MS;
}

export function getHeaderModifiedSnapshot(
  deviceId: string
): { sys___active__modified: any; sys___connected__modified: any; 'cabinet.operation_state': any } | null {
  const entry = headerModifiedCache.get(deviceId);
  if (!entry) return null;
  return {
    sys___active__modified: entry.sys___active__modified,
    sys___connected__modified: entry.sys___connected__modified,
    'cabinet.operation_state': entry['cabinet.operation_state']
  };
}

export function primeHeaderModifiedSnapshot(
  deviceId: string,
  row: { sys___active__modified?: any; sys___connected__modified?: any; 'cabinet.operation_state'?: any }
): void {
  if (!deviceId || !row) return;
  headerModifiedCache.set(deviceId, {
    sys___active__modified: row.sys___active__modified,
    sys___connected__modified: row.sys___connected__modified,
    'cabinet.operation_state': row['cabinet.operation_state'],
    fetchedAt: Date.now()
  });
}

/**
 * Fetch all commissioned fixtures whose meta.device.cabinet_id matches the
 * given human cabinet id. Returns an array (never null).
 */
export async function fetchCabinetFixtures(
  params: FetchParams & { cabinetHumanId: string }
): Promise<any[]> {
  const { companyId, projectId, cabinetHumanId, defaultLocation } = params;
  if (!cabinetHumanId) return [];
  const body: any = {
    columns: FIXTURE_COLUMNS,
    where: [
      "meta.category.category='FIXTURE'",
      'meta.commission.commissioned=true',
      `meta.device.cabinet_id='${cabinetHumanId}'`
    ],
    items: STREAM_ITEMS_LIMIT,
    version: REPORTING_QUERY_VERSION
  };
  const result = await API.streamingPost(
    Types.COMPANIES,
    reportingPath(companyId, projectId),
    body,
    {}
  );
  const list = Array.isArray(result) ? result : [];
  list.forEach((row: any) => setDeviceCommonFields(row, defaultLocation));
  return list;
}

/**
 * Read a `<stream>__modified` column value (epoch-seconds-with-millis number,
 * e.g. 1772725062.974) and format it as the same display string the legacy
 * `/latest/` endpoint produced.
 */
export function formatModified(value: any, timezone: string): string {
  if (value === null || value === undefined || value === '') return 'N/A';
  const num = Number(value);
  if (isNaN(num) || num <= 0) return 'N/A';
  // Reporting-query __modified is seconds (with optional millis).
  const ms = Math.floor(num * 1000);
  return Utils.convertTimestamp(ms, 'DD.MM.YYYY HH:mm', timezone) || 'N/A';
}

/** Raw seconds-with-millis number (e.g. 1772725062.974) suitable for relative-time helpers. */
export function getModifiedSeconds(row: any, streamName: string): number | null {
  if (!row) return null;
  const v = row[`${streamName}__modified`];
  if (v === null || v === undefined || v === '') return null;
  const num = Number(v);
  return isNaN(num) ? null : num;
}

/** Milliseconds suitable for `Utils.getRelativeTime`. */
export function getModifiedMs(row: any, streamName: string): number | null {
  const sec = getModifiedSeconds(row, streamName);
  if (sec === null) return null;
  return Math.floor(sec * 1000);
}
